import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grape, HelpCircle, Check, Building2, Upload, ShieldCheck, Lock, FileText, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/Button';
export function ConnectAccountsPage() {
  const navigate = useNavigate();
  const [connectedApps, setConnectedApps] = useState<string[]>([]);
  const toggleApp = (appName: string) => {
    if (connectedApps.includes(appName)) {
      setConnectedApps(connectedApps.filter(app => app !== appName));
    } else {
      setConnectedApps([...connectedApps, appName]);
    }
  };
  const integrations = [{
    id: 'quickbooks',
    name: 'QuickBooks',
    color: 'bg-green-100 text-green-700',
    desc: 'Sync invoices, expenses, and chart of accounts'
  }, {
    id: 'xero',
    name: 'Xero',
    color: 'bg-blue-100 text-blue-700',
    desc: 'Import transactions and reconciliation data'
  }, {
    id: 'freshbooks',
    name: 'FreshBooks',
    color: 'bg-emerald-100 text-emerald-700',
    desc: 'Connect invoices and expense tracking'
  }, {
    id: 'sage',
    name: 'Sage',
    color: 'bg-teal-100 text-teal-700',
    desc: 'Sync your Sage accounting data'
  }];
  const isContinueEnabled = connectedApps.length > 0;
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-25">
      {/* Simplified Top Bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-26">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center" data-id="element-27">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')} data-id="element-28">

            <div className="bg-violet-100 p-1.5 rounded-lg mr-2" data-id="element-29">
              <Grape className="h-5 w-5 text-violet-600" data-id="element-30" />
            </div>
            <span className="text-lg font-bold text-gray-900 tracking-tight" data-id="element-31">
              Berry Finance
            </span>
          </div>
          <button className="text-sm font-medium text-gray-500 hover:text-gray-900 flex items-center gap-1.5 transition-colors" data-id="element-32">
            <HelpCircle className="h-4 w-4" data-id="element-33" />
            Need help?
          </button>
        </div>
      </header>

      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8" data-id="element-34">
        <div className="max-w-3xl mx-auto" data-id="element-35">
          {/* Progress Stepper */}
          <div className="mb-12" data-id="element-36">
            <div className="flex items-center justify-between relative" data-id="element-37">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-gray-200 -z-10" data-id="element-38"></div>

              {/* Step 1 */}
              <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-39">
                <div className="w-8 h-8 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-40">
                  1
                </div>
                <span className="text-xs font-bold text-violet-600 uppercase tracking-wide" data-id="element-41">
                  Connect
                </span>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-42">
                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-43">
                  2
                </div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-44">
                  Upload
                </span>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center bg-gray-50 px-2" data-id="element-45">
                <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center font-bold text-sm mb-2 ring-4 ring-gray-50" data-id="element-46">
                  3
                </div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-47">
                  Reconcile
                </span>
              </div>
            </div>
          </div>

          {/* Page Header */}
          <div className="text-center mb-10" data-id="element-48">
            <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-49">
              Connect your accounts
            </h1>
            <p className="text-lg text-gray-600 max-w-xl mx-auto" data-id="element-50">
              Link your accounting software and bank accounts to get started.
              Berry will handle the rest.
            </p>
          </div>

          {/* Accounting Software Section */}
          <div className="mb-10" data-id="element-51">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-52">
              <FileText className="h-4 w-4 text-gray-400" data-id="element-53" />
              Accounting Software
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-54">
              {integrations.map(app => {
              const isConnected = connectedApps.includes(app.id);
              return <div key={app.id} className={`
                      relative p-5 rounded-xl border transition-all duration-200 bg-white
                      ${isConnected ? 'border-violet-500 ring-1 ring-violet-500 shadow-sm' : 'border-gray-200 hover:border-violet-300 hover:shadow-md'}
                    `} data-id="element-55">

                    <div className="flex justify-between items-start mb-3" data-id="element-56">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold ${app.color}`} data-id="element-57">

                        {app.name.charAt(0)}
                      </div>
                      {isConnected && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-58">
                          <Check className="h-3 w-3 mr-1" data-id="element-59" />
                          Connected
                        </span>}
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-60">{app.name}</h3>
                    <p className="text-sm text-gray-500 mb-4 h-10" data-id="element-61">
                      {app.desc}
                    </p>
                    <Button variant={isConnected ? 'outline' : 'outline'} size="sm" fullWidth onClick={() => toggleApp(app.id)} className={isConnected ? 'border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50' : 'border-gray-300'} data-id="element-62">

                      {isConnected ? 'Disconnect' : 'Connect'}
                    </Button>
                  </div>;
            })}
            </div>
          </div>

          {/* Bank Accounts Section */}
          <div className="mb-12" data-id="element-63">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-64">
              <Building2 className="h-4 w-4 text-gray-400" data-id="element-65" />
              Bank Accounts
            </h2>
            <div className="space-y-4" data-id="element-66">
              {/* Direct Connect Card */}
              <div className="relative p-6 rounded-xl border border-violet-200 bg-violet-50/50 hover:bg-violet-50 transition-colors" data-id="element-67">
                <div className="absolute top-4 right-4" data-id="element-68">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800" data-id="element-69">
                    Recommended
                  </span>
                </div>
                <div className="flex items-start gap-4" data-id="element-70">
                  <div className="p-3 bg-white rounded-lg shadow-sm border border-violet-100" data-id="element-71">
                    <Building2 className="h-6 w-6 text-violet-600" data-id="element-72" />
                  </div>
                  <div className="flex-1" data-id="element-73">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-74">
                      Connect your bank directly
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 max-w-md" data-id="element-75">
                      Securely link your bank for automatic transaction feeds.
                      Supports 10,000+ institutions including Chase, Wells
                      Fargo, and more.
                    </p>
                    <Button variant="primary" onClick={() => toggleApp('bank-direct')} className={connectedApps.includes('bank-direct') ? 'bg-green-600 hover:bg-green-700' : ''} data-id="element-76">

                      {connectedApps.includes('bank-direct') ? <>
                          <Check className="h-4 w-4 mr-2" data-id="element-77" />
                          Bank Connected
                        </> : 'Connect Bank'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Manual Upload Card */}
              <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors" data-id="element-78">
                <div className="flex items-start gap-4" data-id="element-79">
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-80">
                    <Upload className="h-6 w-6 text-gray-500" data-id="element-81" />
                  </div>
                  <div className="flex-1" data-id="element-82">
                    <h3 className="font-bold text-gray-900 mb-1" data-id="element-83">
                      Upload bank statements
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 max-w-md" data-id="element-84">
                      Manually upload CSV, OFX, or PDF bank statements for
                      reconciliation. Good for historical data.
                    </p>
                    <Button variant="outline" onClick={() => toggleApp('bank-upload')} data-id="element-85">

                      {connectedApps.includes('bank-upload') ? 'Files Uploaded' : 'Upload Files'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Security Trust Bar */}
          <div className="flex flex-wrap justify-center gap-6 text-xs text-gray-500 font-medium border-t border-gray-200 pt-8" data-id="element-86">
            <div className="flex items-center" data-id="element-87">
              <Lock className="h-3 w-3 mr-1.5" data-id="element-88" />
              Bank-level encryption
            </div>
            <div className="flex items-center" data-id="element-89">
              <ShieldCheck className="h-3 w-3 mr-1.5" data-id="element-90" />
              SOC 2 Compliant
            </div>
            <div className="flex items-center" data-id="element-91">
              <Check className="h-3 w-3 mr-1.5" data-id="element-92" />
              Read-only access
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Action Bar */}
      <div className="bg-white border-t border-gray-200 py-4 px-4 sm:px-6 lg:px-8 sticky bottom-0 z-10" data-id="element-93">
        <div className="max-w-3xl mx-auto flex justify-between items-center" data-id="element-94">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-900" data-id="element-95">

            Skip for now
          </Button>
          <div className="flex items-center gap-4" data-id="element-96">
            <span className="text-sm text-gray-500 hidden sm:inline" data-id="element-97">
              {connectedApps.length === 0 ? 'Select at least one account' : `${connectedApps.length} account${connectedApps.length !== 1 ? 's' : ''} selected`}
            </span>
            <Button variant="primary" disabled={!isContinueEnabled} onClick={() => navigate('/statements')} className="min-w-[140px]" data-id="element-98">

              Continue
              <ArrowRight className="ml-2 h-4 w-4" data-id="element-99" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
}